import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ThemeProvider } from "@/providers/ThemeProvider";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { environment } from "@/config/env";

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const { accessToken } = useParams<{ accessToken?: string }>();
  const location = useLocation();
  const { t } = useTranslation("checkout");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(true);

  const verificationAttempted = useRef(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const esito = searchParams.get("esito");

    // If returning from Nexi payment gateway
    if (esito !== null) {
      if (verificationAttempted.current) return;
      verificationAttempted.current = true;

      if (esito !== "OK") {
        setSuccess(false);
        setError("La transazione di pagamento è stata annullata o rifiutata.");
        return;
      }

      setLoading(true);
      axios.defaults.baseURL = environment.ELITE_BASE_URI;

      const paramsObj: Record<string, string> = {};
      searchParams.forEach((value, key) => {
        paramsObj[key] = value;
      });

      axios
        .post("/Estimate/complete-nexi-payment", {
          token: accessToken,
          params: paramsObj,
        })
        .then((res) => {
          if (res.data?.success) {
            setSuccess(true);
            setError(null);
          } else {
            setSuccess(false);
            setError(
              res.data?.message || "Errore nella registrazione del pagamento.",
            );
          }
        })
        .catch((err) => {
          setSuccess(false);
          setError(
            err.response?.data?.message ||
              "Si è verificato un errore durante la registrazione del pagamento.",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [location.search, accessToken]);

  useDocumentTitle(success ? "Prenotazione Confermata" : "Esito Pagamento");

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-md"
        >
          <Card>
            <CardContent className="p-8 flex flex-col items-center text-center gap-6">
              {loading ? (
                <>
                  <Loader2 className="h-16 w-16 text-primary animate-spin" />
                  <div className="space-y-2">
                    <h1 className="text-xl font-bold">
                      Verifica pagamento in corso...
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      Stiamo comunicando con i sistemi di pagamento per
                      confermare la tua prenotazione.
                    </p>
                  </div>
                </>
              ) : success ? (
                <>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.15, type: "spring", stiffness: 200 }}
                  >
                    <CheckCircle className="h-16 w-16 text-green-500" />
                  </motion.div>

                  <div className="space-y-2">
                    <h1 className="text-2xl font-bold">{t("successTitle")}</h1>
                    <p className="text-muted-foreground">
                      {t("successMessage")}
                    </p>
                  </div>

                  <Button
                    size="lg"
                    className="w-full"
                    onClick={() =>
                      navigate(
                        accessToken
                          ? `/preventive-confirmation/${accessToken}`
                          : "/",
                      )
                    }
                  >
                    {t("closePage") || "Visualizza dettagli preventivo"}
                  </Button>
                </>
              ) : (
                <>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.15, type: "spring", stiffness: 200 }}
                  >
                    <AlertCircle className="h-16 w-16 text-destructive" />
                  </motion.div>

                  <div className="space-y-2">
                    <h1 className="text-2xl font-bold text-destructive">
                      Pagamento non completato
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      {error ||
                        "La transazione non è andata a buon fine. La prenotazione non è stata confermata e la camera non è stata bloccata."}
                    </p>
                  </div>

                  <div className="flex flex-col gap-2 w-full">
                    {accessToken && (
                      <Button
                        size="lg"
                        className="w-full"
                        onClick={() => navigate(`/checkout/${accessToken}`)}
                      >
                        Riprova il pagamento
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="lg"
                      className="w-full"
                      onClick={() =>
                        navigate(
                          accessToken
                            ? `/preventive-detail/${accessToken}`
                            : "/",
                        )
                      }
                    >
                      Torna al preventivo
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </ThemeProvider>
  );
}
